import React, { useEffect, useRef, useState } from 'react';
import {
  applicationStatements,
  retryStatement,
  statementDraft,
  statementError,
  StatementCapture,
  uploadStatement
} from '../../../services/statementIq';
import './StatementIqPanel.css';
import { useStatementIqSettings } from '../../../services/statementIqSettings';

const money = (value?: number) => value == null
  ? 'Not supplied'
  : new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(value);
const busyStates = ['UPLOADING', 'STORING', 'QUEUED', 'SUBMITTING', 'PROCESSING'];
const statusLabel = (status: string) => status === 'READY' ? 'Ready'
  : status === 'UPLOADING' || status === 'STORING' ? 'Saving upload'
  : busyStates.includes(status) ? 'Processing'
  : status === 'TIMED_OUT' ? 'Taking longer than expected'
  : status === 'SUBMISSION_UNKNOWN' ? 'Submission needs checking'
  : status === 'UPLOAD_FAILED' ? 'Upload failed' : 'Failed';

type StatementIqPanelProps = {
  applicantId?: string;
  draftId?: string;
  onDraft?: (id: string) => void;
  onReset?: () => void;
  applicationId?: string;
  readOnly?: boolean;
};

export default function StatementIqPanel(props: StatementIqPanelProps) {
  const settings = useStatementIqSettings();
  if (settings.isPending) return null;
  if (settings.isError) return <p role="alert" className="text-danger">Bank statement analysis settings could not be loaded.</p>;
  if (!settings.data?.enabled && (!props.applicationId || !props.readOnly)) return null;
  return <StatementIqCapturePanel {...props} readOnly={props.readOnly || !settings.data?.enabled} />;
}

function StatementIqCapturePanel({
  applicantId,
  draftId,
  onDraft,
  onReset,
  applicationId,
  readOnly = false
}: StatementIqPanelProps) {
  const [captures, setCaptures] = useState<StatementCapture[]>([]);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const draft = useRef(draftId || crypto.randomUUID());
  const request = useRef<{ file: File; id: string } | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = useState<string>();
  const alive = useRef(true);
  const context = useRef({ applicationId, applicantId });
  context.current = { applicationId, applicantId };

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  useEffect(() => {
    setCaptures([]);
    setSelected(undefined);
    setLoaded(false);
    setError('');
    setLoadError('');
  }, [applicationId, draftId, applicantId]);

  useEffect(() => {
    setFile(null);
    request.current = null;
    if (fileInput.current) fileInput.current.value = '';
  }, [applicationId, applicantId]);

  useEffect(() => {
    if (draftId) draft.current = draftId;
    const validApplicantId = /^[0-9]{13}$/.test(applicantId || '');
    // The draft reference is retained before upload; the server creates it during upload.
    if (busy) return;
    if (!applicationId && (!draftId || !validApplicantId)) return;
    setLoaded(false);
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const refresh = async () => {
      try {
        const rows = await (applicationId
          ? applicationStatements(applicationId)
          : statementDraft(draftId!, applicantId));
        if (cancelled) return;
        setCaptures(rows);
        setLoaded(true);
        setLoadError('');
        if (rows.some(row => busyStates.includes(row.status))) timer = setTimeout(refresh, 5000);
      } catch (requestError) {
        if (!cancelled) {
          setLoadError(statementError(requestError));
          timer = setTimeout(refresh, 15000);
        }
      }
    };
    refresh();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [applicationId, draftId, applicantId, busy]);

  const upload = async () => {
    if (!file || !applicantId || !/^[0-9]{13}$/.test(applicantId)) return;
    if (file.type !== 'application/pdf' || file.size > 10 * 1024 * 1024) {
      setError('Choose a PDF up to 10 MiB.');
      return;
    }
    if (request.current?.file !== file) request.current = { file, id: crypto.randomUUID() };
    const uploadDraft = draft.current;
    const isCurrent = () => alive.current && context.current.applicationId === applicationId
      && context.current.applicantId === applicantId && draft.current === uploadDraft;
    onDraft?.(uploadDraft);
    setBusy(true);
    setError('');
    setLoadError('');
    try {
      const saved = await uploadStatement(uploadDraft, request.current.id, applicantId, file);
      if (!isCurrent()) return;
      onDraft?.(uploadDraft);
      setCaptures(old => [...old.filter(row => row.id !== saved.id), saved]);
      setFile(null);
      request.current = null;
      if (fileInput.current) fileInput.current.value = '';
    } catch (requestError) {
      if (isCurrent()) setError(statementError(requestError));
    } finally {
      if (alive.current) setBusy(false);
    }
  };

  const analysisInProgress = captures.some(capture => busyStates.includes(capture.status));

  return (
    <section className="card my-3" aria-label="Bank statement analysis">
      <div className="card-body">
        <h6>Bank statement analysis</h6>
        {analysisInProgress && <div className="statement-analysis-status" role="status" aria-label="Statement analysis in progress" aria-live="polite">
          <span className="spinner-border spinner-border-sm text-warning" aria-hidden="true"></span>
          <span className="statement-analysis-status__dot" aria-hidden="true"></span>
          <div>
            <strong>Statement analysis in progress</strong>
            <p className="mb-0 small">This can take a few minutes. You can continue to Loan Request, Bank Details and Review while we analyse the statement. This panel updates automatically.</p>
          </div>
        </div>}
        {!applicationId && !readOnly && <>
          <p className="text-muted">Upload bank statements now. Analysis runs while you complete the application; you won’t need to upload them again after submission.</p>
          <label className="form-label" htmlFor="early-bank-statement">Bank statement (PDF, up to 10 MiB; up to six files)</label>
          <input ref={fileInput} id="early-bank-statement" type="file" accept="application/pdf,.pdf" className="d-none" disabled={busy}
            onChange={event => setFile(event.target.files?.[0] || null)} />
          <div className="d-flex flex-wrap align-items-center gap-2">
            <label htmlFor="early-bank-statement" className={`btn btn-outline-primary mb-0 ${busy ? 'disabled' : ''}`}>
              <i className={`fa ${file ? 'fa-check' : 'fa-upload'} me-2`}></i>{file ? 'Replace file' : 'Browse files'}
            </label>
            {file && <span className="small text-muted text-break">{file.name}</span>}
          </div>
          <button type="button" className="btn btn-primary mt-2" disabled={busy || !file || !/^[0-9]{13}$/.test(applicantId || '')}
            onClick={upload}>{busy ? 'Uploading…' : 'Upload and analyse'}</button>
          {!/^[0-9]{13}$/.test(applicantId || '') && <p className="form-text">Enter the applicant’s ID number first.</p>}
          {draftId && onReset && <button type="button" className="btn btn-link ms-2" disabled={busy} onClick={() => {
            if (window.confirm('Start a new statement set? The previous files remain stored but will not be attached to this application.')) {
              draft.current = crypto.randomUUID();
              setCaptures([]);
              setError('');
              setLoadError('');
              setFile(null);
              request.current = null;
              onReset();
              setSelected(undefined);
              if (fileInput.current) fileInput.current.value = '';
            }
          }}>Start a new statement set</button>}
        </>}
        {error && <p role="alert" className="text-danger mt-2">{error}</p>}
        {loadError && <p role="alert" className="text-danger mt-2">{loadError}</p>}
        {captures.map(capture => <div className="border-top mt-3 pt-3" key={capture.id}>
          <strong>{capture.name}</strong> <span role="status">— {statusLabel(capture.status)}</span>
          {capture.status === 'READY' && <button type="button" className="btn btn-outline-primary btn-sm ms-2" onClick={() => setSelected(selected === capture.id ? undefined : capture.id)}>{selected === capture.id ? 'Hide results' : 'View results'}</button>}
          {capture.status === 'TIMED_OUT' && !applicationId && <button type="button" className="btn btn-outline-secondary btn-sm ms-2" onClick={async () => {
            setBusy(true);
            try { await retryStatement(capture.draftId, capture.id); } catch (requestError) { setError(statementError(requestError)); } finally { setBusy(false); }
          }}>Check again</button>}
          {capture.status === 'SUBMISSION_UNKNOWN' && <p>We cannot confirm whether the provider accepted this file. Ask support to reconcile it before sending it again. Reference: {capture.id}</p>}
          {capture.status === 'UPLOAD_FAILED' && <p>Retry uploading the same file. The application has not been submitted.</p>}
          {capture.status === 'FAILED' && <p>StatementIQ could not process this statement. Check the PDF and upload a corrected statement.</p>}
          {selected === capture.id && capture.result && <div className="mt-3">
            <p>StatementIQ analysis — review against the supplied bank statement. This is not a credit approval.</p>
            <div className="table-responsive"><table className="table table-sm"><thead><tr><th>Period</th><th>Credits</th><th>Debits</th><th>Net</th><th>Detected salary</th><th>Returned debit orders</th><th>Lowest balance</th></tr></thead>
              <tbody>{capture.result.monthly_summary?.map((month, index) => <tr key={index}><td>{index + 1}</td><td>{money(month.total_credits)}</td><td>{money(month.total_debits)}</td><td>{money(month.net)}</td><td>{month.salary?.detected ? money(month.salary.amount) : 'Not detected'}</td><td>{month.returned_debit_orders ?? 'Not supplied'}</td><td>{money(month.lowest_balance)}</td></tr>)}</tbody></table></div>
            {capture.result.fraud && <p>Fraud indicators: {capture.result.fraud.risk_level || 'Not supplied'} — {capture.result.fraud.summary || capture.result.fraud.recommendation}</p>}
            {capture.result.warnings?.map((warning, index) => <p key={index} className="text-warning">{warning}</p>)}
          </div>}
        </div>)}
        {applicationId && loaded && !captures.length && !error && !loadError && <p>No early statement analysis is linked to this application.</p>}
      </div>
    </section>
  );
}
